/**
 * Fast, sharp Canvas-2D renderers for the tabular statistics widgets.
 *
 * These mirror the *native* pdfmake rendering used by the PDF export
 * (`statistics-pdf-export.service.ts`) — vector text, grouped/indented rows,
 * multi-column layout and the average-alert-durations stacked bar — but draw
 * straight onto a canvas instead of rasterizing the live DOM with html2canvas.
 *
 * The per-widget PNG download previously always went through html2canvas, which
 * is the slow path testers complained about. For widgets that already have a
 * native PDF representation we can render the same structured data here in a
 * few milliseconds and stay crisp.
 *
 * Inputs take already-translated strings (title, column headers) so this module
 * stays pure and trivially unit-testable — the calling widget resolves i18n.
 */

import type { StatisticsExportSnapshot } from './statistics-export.models';

export interface NativeTableColumnRender {
  header: string;
  align?: 'left' | 'right';
  width?: number | '*' | 'auto';
}

export interface NativeTableRowRender {
  cells: string[];
  /** Bold dark-blue group-header row (e.g. a device-type subtotal). */
  group?: boolean;
  /** Indent the first cell (a child item under a group header). */
  indent?: boolean;
}

export interface NativeTableRenderInput {
  title?: string;
  /** Large emphasised value under the title (e.g. total alerts). */
  headlineValue?: string;
  columns: NativeTableColumnRender[];
  rows: NativeTableRowRender[];
  /** Lay rows out across N side-by-side column groups (column-major). Default 1. */
  columnGroups?: number;
}

export interface AvgDurationSegmentRender {
  label: string;
  value: string;
  seconds: number;
  color: string;
}

export interface AvgDurationsRenderInput {
  title?: string;
  totalLabel: string;
  totalValue: string;
  segments: AvgDurationSegmentRender[];
}

/** A single proportional average-time bar (fraction 0..1 of the longest value). */
export interface CareRecipientBarRender {
  label: string;
  value: string;
  fraction: number;
}

export interface CareRecipientReasonsRender {
  title: string;
  /** Stacked bar segments (fraction 0..1 of the total) with cycled colours. */
  segments: { fraction: number; color: string }[];
  /** "<reason> <pct>%" info lines under the bar. */
  labels: string[];
  /** Shown instead of the labels when there are no alarm reasons. */
  emptyLabel?: string;
}

export interface CareRecipientNoteRender {
  time: string;
  comment: string;
}

export interface CareRecipientCardRenderInput {
  name: string;
  /** Total-alerts headline (count + translated "Alerts"), when the card shows it. */
  alerts?: { count: string; label: string };
  avgWait?: CareRecipientBarRender;
  avgCare?: CareRecipientBarRender;
  reasons?: CareRecipientReasonsRender;
  notes: CareRecipientNoteRender[];
}

export interface StatCardRowRender {
  label: string;
  value: string;
}

/**
 * A simple statistics card: coloured background, optional title, an optional
 * big headline number, and label/value rows. Covers the dark-blue
 * care-recipient counter and the grey response-time stats card.
 */
export interface StatCardRenderInput {
  title?: string;
  /** Large emphasised number (e.g. the care-recipient count). */
  headline?: string;
  rows?: StatCardRowRender[];
  background: string;
  foreground: string;
  widthCss?: number;
}

const FONT_FAMILY = '"Roboto", "Helvetica Neue", Arial, sans-serif';
const DARK_BLUE = '#07184D';
const GREY = '#707070';
const BODY_COLOR = '#333333';
const SEPARATOR_COLOR = '#e2e5ec';
const BACKGROUND = '#ffffff';

/** Rendered at 2x for crisp text on high-DPI screens (still instant — it's vector text). */
const RENDER_SCALE = 2;
/**
 * Browsers cap a single canvas dimension (~16384px in Chrome). A very tall
 * table (e.g. a large care-recipient device list) could otherwise exceed it and
 * silently render blank; prepareContext scales down to stay under this bound.
 */
const SAFE_CANVAS_MAX_PX = 16000;
const OUTER_PADDING = 28;

const TITLE_FONT_PX = 16;
const HEADLINE_FONT_PX = 26;
const HEADER_FONT_PX = 14;
const BODY_FONT_PX = 14;

const ROW_HEIGHT = 24;
const HEADER_ROW_HEIGHT = 26;
const CELL_PADDING_X = 8;
const GROUP_SPACER = 28;
const INDENT_PX = 16;

/** Keep the flexible label column bounded so one long name can't blow up the canvas. */
const MAX_STAR_COLUMN_WIDTH = 340;
const MIN_STAR_COLUMN_WIDTH = 120;
const MIN_CONTENT_WIDTH = 860;

const AVG_BAR_HEIGHT = 26;
const AVG_CARD_GAP = 14;
const AVG_CARD_ACCENT_HEIGHT = 4;
const AVG_LABEL_FONT_PX = 13;
const AVG_VALUE_FONT_PX = 20;

// Care-recipient-card renderer (mirrors care-recipient-card.component.css).
const CRC_TEXT = '#0b2356';
const CRC_LIST = '#1e407f';
const CRC_BAR = '#07184D';
const CRC_BAR_TRACK = '#e5e8ef';
const CRC_WIDTH_CSS = 480;
const CRC_PAD_LEFT = 24;
const CRC_PAD_RIGHT = 30;
const CRC_PAD_TOP = 18;
const CRC_PAD_BOTTOM = 24;
const CRC_NAME_FONT_PX = 24;
const CRC_ALERT_NUM_FONT_PX = 28;
const CRC_ALERT_LABEL_FONT_PX = 16;
const CRC_SECTION_FONT_PX = 15;
const CRC_BAR_HEIGHT = 10;
const CRC_REASON_LABEL_FONT_PX = 14;
const CRC_NOTE_TIME_FONT_PX = 13;
const CRC_NOTE_COMMENT_FONT_PX = 14;
const CRC_LINE_GAP = 4;
const CRC_SECTION_GAP = 18;

// Simple stat card (care-recipient counter, response-time stats).
const STAT_CARD_WIDTH_CSS = 360;
const STAT_CARD_PAD = 24;
const STAT_TITLE_FONT_PX = 16;
const STAT_HEADLINE_FONT_PX = 34;
const STAT_ROW_LABEL_FONT_PX = 16;
const STAT_ROW_VALUE_FONT_PX = 28;
const STAT_TITLE_GAP = 14;
const STAT_ROW_GAP = 12;

function titleFont(): string {
  return `bold ${TITLE_FONT_PX}px ${FONT_FAMILY}`;
}
function headlineFont(): string {
  return `bold ${HEADLINE_FONT_PX}px ${FONT_FAMILY}`;
}
function headerFont(): string {
  return `bold ${HEADER_FONT_PX}px ${FONT_FAMILY}`;
}
function bodyFont(group?: boolean): string {
  return `${group ? 'bold ' : ''}${BODY_FONT_PX}px ${FONT_FAMILY}`;
}

function createCanvas(): HTMLCanvasElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  return document.createElement('canvas');
}

function truncateToWidth(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  // No room at all (callers clamp with Math.max(0, available)) — draw nothing
  // rather than overflow into the neighbouring column/card.
  if (maxWidth <= 0) {
    return '';
  }
  if (ctx.measureText(text).width <= maxWidth) {
    return text;
  }
  const ellipsis = '…';
  // Not even the ellipsis fits → drop the cell entirely instead of overflowing.
  if (ctx.measureText(ellipsis).width > maxWidth) {
    return '';
  }
  // Binary-search the longest prefix that still fits with the ellipsis. Width is
  // monotonic in prefix length, so this needs O(log n) measurements instead of
  // the O(n) per-character chop — matters for long cells in large tables. low=0
  // means only the ellipsis fits, which is preferable to an overflowing char.
  let low = 0;
  let high = text.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (ctx.measureText(text.slice(0, mid) + ellipsis).width <= maxWidth) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }
  return text.slice(0, low) + ellipsis;
}

/** Split rows across N side-by-side groups (column-major); mirrors the PDF export. */
function distributeRowsIntoColumns(
  rows: NativeTableRowRender[],
  groups: number
): NativeTableRowRender[][] {
  if (groups <= 1) {
    return [rows];
  }

  const isGrouped = rows.some((row) => row.group);
  if (!isGrouped) {
    const perColumn = Math.ceil(rows.length / groups);
    return Array.from({ length: groups }, (_, g) => rows.slice(g * perColumn, (g + 1) * perColumn));
  }

  const blocks: NativeTableRowRender[][] = [];
  let current: NativeTableRowRender[] | null = null;
  for (const row of rows) {
    if (row.group || !current) {
      current = [row];
      blocks.push(current);
    } else {
      current.push(row);
    }
  }

  const columns: NativeTableRowRender[][] = Array.from({ length: groups }, () => []);
  for (const block of blocks) {
    let shortest = 0;
    for (let g = 1; g < groups; g++) {
      if (columns[g].length < columns[shortest].length) {
        shortest = g;
      }
    }
    columns[shortest].push(...block);
  }
  return columns;
}

/** Natural (content) width per column, before flexible '*' columns absorb slack. */
function measureColumnWidths(
  ctx: CanvasRenderingContext2D,
  columns: NativeTableColumnRender[],
  columnRows: NativeTableRowRender[][]
): number[] {
  const widths: number[] = [];

  for (let c = 0; c < columns.length; c++) {
    const col = columns[c];

    if (typeof col.width === 'number') {
      widths.push(col.width);
      continue;
    }

    ctx.font = headerFont();
    let contentWidth = ctx.measureText(col.header).width;

    for (const column of columnRows) {
      for (const row of column) {
        ctx.font = bodyFont(row.group);
        const indent = c === 0 && row.indent ? INDENT_PX : 0;
        contentWidth = Math.max(contentWidth, ctx.measureText(row.cells[c] ?? '').width + indent);
      }
    }

    let width = contentWidth + CELL_PADDING_X * 2;
    if (col.width === '*') {
      width = Math.min(Math.max(width, MIN_STAR_COLUMN_WIDTH), MAX_STAR_COLUMN_WIDTH);
    }
    widths.push(width);
  }

  return widths;
}

/**
 * Render a native statistics table (columns/rows, optional grouping, indentation
 * and multi-column layout) to a PNG data URL. Returns null when no canvas is
 * available (SSR) or the 2D context can't be created.
 */
export function renderNativeTableToPng(input: NativeTableRenderInput): string | null {
  const canvas = createCanvas();
  const measureCtx = canvas?.getContext('2d');
  if (!canvas || !measureCtx) {
    return null;
  }

  const groups = Math.max(1, input.columnGroups ?? 1);
  const columnRows = distributeRowsIntoColumns(input.rows, groups);
  const perColumn = columnRows.reduce((max, col) => Math.max(max, col.length), 0);

  const widths = measureColumnWidths(measureCtx, input.columns, columnRows);
  const groupWidth = widths.reduce((sum, w) => sum + w, 0);
  const contentWidth = Math.max(
    MIN_CONTENT_WIDTH,
    groupWidth * groups + GROUP_SPACER * (groups - 1)
  );

  // Distribute any horizontal slack into the '*' columns of every group so the
  // table fills the target width instead of leaving a ragged right edge. This
  // can intentionally take a '*' column past MAX_STAR_COLUMN_WIDTH: that cap
  // only bounds the *content-driven* measurement (so one very long name can't
  // blow up the column), whereas filling to MIN_CONTENT_WIDTH mirrors the
  // full-width tables of the PDF export and the original DOM capture. The math
  // stays exact — finalGroupWidth * groups + spacers === contentWidth.
  const slack = Math.max(0, contentWidth - (groupWidth * groups + GROUP_SPACER * (groups - 1)));
  const starIndexes = input.columns
    .map((col, i) => (col.width === '*' ? i : -1))
    .filter((i) => i >= 0);
  if (starIndexes.length > 0 && slack > 0) {
    const perStar = slack / groups / starIndexes.length;
    for (const i of starIndexes) {
      widths[i] += perStar;
    }
  }
  const finalGroupWidth = widths.reduce((sum, w) => sum + w, 0);

  let cursorY = OUTER_PADDING;
  const titleHeight = input.title ? TITLE_FONT_PX + 12 : 0;
  const headlineHeight = input.headlineValue ? HEADLINE_FONT_PX + 14 : 0;
  const tableHeight = HEADER_ROW_HEIGHT + perColumn * ROW_HEIGHT;
  const heightCss = OUTER_PADDING * 2 + titleHeight + headlineHeight + tableHeight;
  const widthCss = OUTER_PADDING * 2 + contentWidth;

  const ctx = prepareContext(canvas, widthCss, heightCss);
  if (!ctx) {
    return null;
  }

  const left = OUTER_PADDING;

  if (input.title) {
    ctx.fillStyle = DARK_BLUE;
    ctx.font = titleFont();
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.fillText(input.title, left, cursorY + TITLE_FONT_PX);
    cursorY += titleHeight;
  }

  if (input.headlineValue) {
    ctx.fillStyle = DARK_BLUE;
    ctx.font = headlineFont();
    ctx.textAlign = 'left';
    ctx.fillText(input.headlineValue, left, cursorY + HEADLINE_FONT_PX);
    cursorY += headlineHeight;
  }

  // Header + body rows for each side-by-side group.
  for (let g = 0; g < groups; g++) {
    const groupLeft = left + g * (finalGroupWidth + GROUP_SPACER);
    drawTableGroup(ctx, {
      groupLeft,
      top: cursorY,
      columns: input.columns,
      columnWidths: widths,
      rows: columnRows[g] ?? [],
      perColumn,
    });
  }

  return canvas.toDataURL('image/png');
}

function drawTableGroup(
  ctx: CanvasRenderingContext2D,
  params: {
    groupLeft: number;
    top: number;
    columns: NativeTableColumnRender[];
    columnWidths: number[];
    rows: NativeTableRowRender[];
    perColumn: number;
  }
): void {
  const { groupLeft, top, columns, columnWidths, rows, perColumn } = params;
  const groupWidth = columnWidths.reduce((sum, w) => sum + w, 0);

  // Header row.
  ctx.font = headerFont();
  ctx.fillStyle = DARK_BLUE;
  drawRowCells(ctx, {
    rowLeft: groupLeft,
    baselineY: top + HEADER_ROW_HEIGHT - 8,
    columns,
    columnWidths,
    cells: columns.map((col) => col.header),
    indent: false,
  });

  // Separator under the header.
  drawSeparator(ctx, groupLeft, top + HEADER_ROW_HEIGHT, groupWidth);

  for (let i = 0; i < perColumn; i++) {
    const row = rows[i];
    // Shorter columns in a multi-column group have no row here — skip so we
    // don't paint separator lines across their empty tail.
    if (!row) {
      continue;
    }
    const rowTop = top + HEADER_ROW_HEIGHT + i * ROW_HEIGHT;
    ctx.font = bodyFont(row.group);
    ctx.fillStyle = row.group ? DARK_BLUE : BODY_COLOR;
    drawRowCells(ctx, {
      rowLeft: groupLeft,
      baselineY: rowTop + ROW_HEIGHT - 7,
      columns,
      columnWidths,
      cells: row.cells,
      indent: row.indent ?? false,
    });
    drawSeparator(ctx, groupLeft, rowTop + ROW_HEIGHT, groupWidth);
  }
}

function drawRowCells(
  ctx: CanvasRenderingContext2D,
  params: {
    rowLeft: number;
    baselineY: number;
    columns: NativeTableColumnRender[];
    columnWidths: number[];
    cells: string[];
    indent: boolean;
  }
): void {
  const { rowLeft, baselineY, columns, columnWidths, cells, indent } = params;
  let x = rowLeft;
  for (let c = 0; c < columns.length; c++) {
    const colWidth = columnWidths[c];
    const align = columns[c].align ?? 'left';
    const leftInset = c === 0 && indent ? INDENT_PX : 0;
    const available = colWidth - CELL_PADDING_X * 2 - leftInset;
    const text = truncateToWidth(ctx, cells[c] ?? '', Math.max(0, available));

    if (align === 'right') {
      ctx.textAlign = 'right';
      ctx.fillText(text, x + colWidth - CELL_PADDING_X, baselineY);
    } else {
      ctx.textAlign = 'left';
      ctx.fillText(text, x + CELL_PADDING_X + leftInset, baselineY);
    }
    x += colWidth;
  }
}

function drawSeparator(ctx: CanvasRenderingContext2D, x: number, y: number, width: number): void {
  ctx.strokeStyle = SEPARATOR_COLOR;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + 0.5);
  ctx.lineTo(x + width, y + 0.5);
  ctx.stroke();
}

/**
 * Render the average-alert-durations widget (title, total, horizontal stacked
 * bar and KPI cards) natively to a PNG data URL — matches the PDF layout.
 */
export function renderAvgDurationsToPng(input: AvgDurationsRenderInput): string | null {
  const canvas = createCanvas();
  if (!canvas) {
    return null;
  }

  const contentWidth = MIN_CONTENT_WIDTH;
  const titleHeight = input.title ? TITLE_FONT_PX + 12 : 0;
  const totalHeight = BODY_FONT_PX + 12;
  const barBlockHeight = AVG_BAR_HEIGHT + 18;
  const cardBlockHeight = AVG_CARD_ACCENT_HEIGHT + 8 + AVG_LABEL_FONT_PX + 6 + AVG_VALUE_FONT_PX + 4;
  const heightCss = OUTER_PADDING * 2 + titleHeight + totalHeight + barBlockHeight + cardBlockHeight;
  const widthCss = OUTER_PADDING * 2 + contentWidth;

  const ctx = prepareContext(canvas, widthCss, heightCss);
  if (!ctx) {
    return null;
  }

  const left = OUTER_PADDING;
  let cursorY = OUTER_PADDING;

  if (input.title) {
    ctx.fillStyle = DARK_BLUE;
    ctx.font = titleFont();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(input.title, left, cursorY + TITLE_FONT_PX);
    cursorY += titleHeight;
  }

  // "Total alert time: X" — right aligned, grey.
  ctx.fillStyle = GREY;
  ctx.font = `${BODY_FONT_PX}px ${FONT_FAMILY}`;
  ctx.textAlign = 'right';
  ctx.fillText(`${input.totalLabel}: ${input.totalValue}`, left + contentWidth, cursorY + BODY_FONT_PX);
  cursorY += totalHeight;

  // Stacked bar.
  const totalSeconds = input.segments.reduce((sum, seg) => sum + Math.max(0, seg.seconds), 0);
  let barX = left;
  if (totalSeconds > 0) {
    for (const seg of input.segments) {
      const width = (Math.max(0, seg.seconds) / totalSeconds) * contentWidth;
      if (width <= 0) {
        continue;
      }
      ctx.fillStyle = seg.color;
      ctx.fillRect(barX, cursorY, width, AVG_BAR_HEIGHT);
      barX += width;
    }
  } else {
    ctx.fillStyle = '#e5e8ef';
    ctx.fillRect(left, cursorY, contentWidth, AVG_BAR_HEIGHT);
  }
  cursorY += barBlockHeight;

  // KPI cards — one per segment with a coloured accent on top.
  const cardCount = input.segments.length;
  if (cardCount > 0) {
    const cardWidth = (contentWidth - AVG_CARD_GAP * (cardCount - 1)) / cardCount;
    input.segments.forEach((seg, i) => {
      const cardX = left + i * (cardWidth + AVG_CARD_GAP);
      ctx.fillStyle = seg.color;
      ctx.fillRect(cardX, cursorY, cardWidth, AVG_CARD_ACCENT_HEIGHT);

      ctx.fillStyle = GREY;
      ctx.font = `${AVG_LABEL_FONT_PX}px ${FONT_FAMILY}`;
      ctx.textAlign = 'left';
      const labelY = cursorY + AVG_CARD_ACCENT_HEIGHT + 8 + AVG_LABEL_FONT_PX;
      ctx.fillText(truncateToWidth(ctx, seg.label, cardWidth), cardX, labelY);

      ctx.fillStyle = DARK_BLUE;
      ctx.font = `bold ${AVG_VALUE_FONT_PX}px ${FONT_FAMILY}`;
      ctx.fillText(truncateToWidth(ctx, seg.value, cardWidth), cardX, labelY + 6 + AVG_VALUE_FONT_PX);
    });
  }

  return canvas.toDataURL('image/png');
}

/** Word-wrap `text` to `maxWidth`, breaking over-long single words by character. */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const paragraph of String(text ?? '').split('\n')) {
    const words = paragraph.split(/\s+/).filter((w) => w.length > 0);
    if (words.length === 0) {
      lines.push('');
      continue;
    }
    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth) {
        line = candidate;
        continue;
      }
      // Doesn't fit on the current line — flush what we have.
      if (line) {
        lines.push(line);
        line = '';
      }
      // The word fits on a fresh line: start a new line with it.
      if (ctx.measureText(word).width <= maxWidth) {
        line = word;
        continue;
      }
      // A single word wider than maxWidth can't be wrapped on spaces — split it
      // by character (regardless of whether it started the line or followed one).
      let chunk = '';
      for (const ch of word) {
        if (ctx.measureText(chunk + ch).width > maxWidth && chunk) {
          lines.push(chunk);
          chunk = ch;
        } else {
          chunk += ch;
        }
      }
      line = chunk;
    }
    if (line) {
      lines.push(line);
    }
  }
  return lines;
}

/**
 * Render a single care-recipient card (name, optional alert counter, average
 * wait/care proportional bars, most-common-alarm-reason stacked bar + labels,
 * and free-text notes) natively to a PNG data URL — replaces the html2canvas
 * DOM capture for the per-widget PNG download. Returns null when no canvas is
 * available (SSR) or the 2D context can't be created.
 */
export function renderCareRecipientCardToPng(input: CareRecipientCardRenderInput): string | null {
  const canvas = createCanvas();
  const measureCtx = canvas?.getContext('2d');
  if (!canvas || !measureCtx) {
    return null;
  }

  const contentWidth = CRC_WIDTH_CSS - CRC_PAD_LEFT - CRC_PAD_RIGHT;

  // Pre-wrap note comments so the height can be computed before sizing the canvas.
  measureCtx.font = `${CRC_NOTE_COMMENT_FONT_PX}px ${FONT_FAMILY}`;
  const wrappedNotes = input.notes.map((note) => ({
    time: note.time,
    lines: wrapText(measureCtx, note.comment, contentWidth),
  }));

  let height = CRC_PAD_TOP;
  height += CRC_NAME_FONT_PX + CRC_SECTION_GAP; // name
  if (input.alerts) {
    height += CRC_ALERT_NUM_FONT_PX + CRC_SECTION_GAP;
  }
  const barBlockHeight = CRC_SECTION_FONT_PX + CRC_LINE_GAP + CRC_BAR_HEIGHT + CRC_SECTION_GAP;
  if (input.avgWait) height += barBlockHeight;
  if (input.avgCare) height += barBlockHeight;
  if (input.reasons) {
    height += CRC_SECTION_FONT_PX + CRC_LINE_GAP + CRC_BAR_HEIGHT + 8;
    const lineCount = input.reasons.labels.length || (input.reasons.emptyLabel ? 1 : 0);
    height += lineCount * (CRC_REASON_LABEL_FONT_PX + CRC_LINE_GAP) + CRC_SECTION_GAP;
  }
  if (wrappedNotes.length > 0) {
    for (const note of wrappedNotes) {
      height += CRC_NOTE_TIME_FONT_PX + CRC_LINE_GAP;
      height += note.lines.length * (CRC_NOTE_COMMENT_FONT_PX + CRC_LINE_GAP);
      height += 8;
    }
  }
  height += CRC_PAD_BOTTOM;

  const ctx = prepareContext(canvas, CRC_WIDTH_CSS, height);
  if (!ctx) {
    return null;
  }

  const left = CRC_PAD_LEFT;
  let y = CRC_PAD_TOP;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  // Name.
  ctx.fillStyle = CRC_TEXT;
  ctx.font = `${CRC_NAME_FONT_PX}px ${FONT_FAMILY}`;
  ctx.fillText(truncateToWidth(ctx, input.name, contentWidth), left, y + CRC_NAME_FONT_PX);
  y += CRC_NAME_FONT_PX + CRC_SECTION_GAP;

  // Total alerts headline.
  if (input.alerts) {
    ctx.fillStyle = CRC_TEXT;
    ctx.font = `bold ${CRC_ALERT_NUM_FONT_PX}px ${FONT_FAMILY}`;
    const baseline = y + CRC_ALERT_NUM_FONT_PX;
    ctx.fillText(input.alerts.count, left, baseline);
    const countWidth = ctx.measureText(input.alerts.count).width;
    ctx.font = `${CRC_ALERT_LABEL_FONT_PX}px ${FONT_FAMILY}`;
    ctx.fillText(input.alerts.label, left + countWidth + 8, baseline);
    y += CRC_ALERT_NUM_FONT_PX + CRC_SECTION_GAP;
  }

  const drawAvgBar = (bar: CareRecipientBarRender): void => {
    ctx.fillStyle = CRC_TEXT;
    ctx.font = `${CRC_SECTION_FONT_PX}px ${FONT_FAMILY}`;
    ctx.fillText(`${bar.label}  |  ${bar.value}`, left, y + CRC_SECTION_FONT_PX);
    y += CRC_SECTION_FONT_PX + CRC_LINE_GAP;
    ctx.fillStyle = CRC_BAR_TRACK;
    ctx.fillRect(left, y, contentWidth, CRC_BAR_HEIGHT);
    const fillWidth = Math.max(0, Math.min(1, bar.fraction)) * contentWidth;
    ctx.fillStyle = CRC_BAR;
    ctx.fillRect(left, y, fillWidth, CRC_BAR_HEIGHT);
    y += CRC_BAR_HEIGHT + CRC_SECTION_GAP;
  };
  if (input.avgWait) drawAvgBar(input.avgWait);
  if (input.avgCare) drawAvgBar(input.avgCare);

  // Most common alarm reasons.
  if (input.reasons) {
    ctx.fillStyle = CRC_TEXT;
    ctx.font = `${CRC_SECTION_FONT_PX}px ${FONT_FAMILY}`;
    ctx.fillText(input.reasons.title, left, y + CRC_SECTION_FONT_PX);
    y += CRC_SECTION_FONT_PX + CRC_LINE_GAP;

    // Paint the full track first, then the coloured segments on top — so any
    // remainder (segments not summing to 1, or no segments at all) shows the
    // track colour instead of the white canvas background.
    ctx.fillStyle = CRC_BAR_TRACK;
    ctx.fillRect(left, y, contentWidth, CRC_BAR_HEIGHT);
    let segX = left;
    for (const seg of input.reasons.segments) {
      const w = Math.max(0, Math.min(1, seg.fraction)) * contentWidth;
      if (w <= 0) continue;
      ctx.fillStyle = seg.color;
      ctx.fillRect(segX, y, w, CRC_BAR_HEIGHT);
      segX += w;
    }
    y += CRC_BAR_HEIGHT + 8;

    ctx.font = `500 ${CRC_REASON_LABEL_FONT_PX}px ${FONT_FAMILY}`;
    ctx.fillStyle = CRC_LIST;
    const labels = input.reasons.labels.length
      ? input.reasons.labels
      : input.reasons.emptyLabel
        ? [input.reasons.emptyLabel]
        : [];
    for (const label of labels) {
      ctx.fillText(truncateToWidth(ctx, label, contentWidth), left, y + CRC_REASON_LABEL_FONT_PX);
      y += CRC_REASON_LABEL_FONT_PX + CRC_LINE_GAP;
    }
    y += CRC_SECTION_GAP;
  }

  // Notes.
  for (const note of wrappedNotes) {
    ctx.fillStyle = CRC_TEXT;
    ctx.font = `bold ${CRC_NOTE_TIME_FONT_PX}px ${FONT_FAMILY}`;
    ctx.fillText(note.time, left, y + CRC_NOTE_TIME_FONT_PX);
    y += CRC_NOTE_TIME_FONT_PX + CRC_LINE_GAP;

    ctx.font = `${CRC_NOTE_COMMENT_FONT_PX}px ${FONT_FAMILY}`;
    for (const line of note.lines) {
      ctx.fillText(line, left, y + CRC_NOTE_COMMENT_FONT_PX);
      y += CRC_NOTE_COMMENT_FONT_PX + CRC_LINE_GAP;
    }
    y += 8;
  }

  return canvas.toDataURL('image/png');
}

/**
 * Render a simple statistics card (coloured background, optional title, an
 * optional big headline number, and label/value rows) natively to a PNG data
 * URL. Replaces the html2canvas capture for the care-recipient counter and the
 * response-time stats card. Returns null when no canvas is available.
 */
export function renderStatCardToPng(input: StatCardRenderInput): string | null {
  const canvas = createCanvas();
  const measureCtx = canvas?.getContext('2d');
  if (!canvas || !measureCtx) {
    return null;
  }
  const rows = input.rows ?? [];

  // Canvas text isn't clipped to the fill rect, so a long localized label or a
  // wide value would paint past a fixed-width card. Grow the card to the widest
  // line instead (never below the default width) so content always fits.
  let maxLineWidth = 0;
  if (input.title) {
    measureCtx.font = `${STAT_TITLE_FONT_PX}px ${FONT_FAMILY}`;
    maxLineWidth = Math.max(maxLineWidth, measureCtx.measureText(input.title).width);
  }
  if (input.headline) {
    measureCtx.font = `bold ${STAT_HEADLINE_FONT_PX}px ${FONT_FAMILY}`;
    maxLineWidth = Math.max(maxLineWidth, measureCtx.measureText(input.headline).width);
  }
  for (const row of rows) {
    measureCtx.font = `600 ${STAT_ROW_LABEL_FONT_PX}px ${FONT_FAMILY}`;
    const labelWidth = measureCtx.measureText(`${row.label}:`).width;
    measureCtx.font = `bold ${STAT_ROW_VALUE_FONT_PX}px ${FONT_FAMILY}`;
    const valueWidth = measureCtx.measureText(row.value).width;
    maxLineWidth = Math.max(maxLineWidth, labelWidth + 8 + valueWidth);
  }
  const width = Math.max(
    input.widthCss ?? STAT_CARD_WIDTH_CSS,
    Math.ceil(maxLineWidth + STAT_CARD_PAD * 2)
  );

  let height = STAT_CARD_PAD;
  if (input.title) {
    height += STAT_TITLE_FONT_PX + STAT_TITLE_GAP;
  }
  if (input.headline) {
    height += STAT_HEADLINE_FONT_PX + (rows.length ? STAT_TITLE_GAP : 0);
  }
  rows.forEach((_, i) => {
    height += STAT_ROW_VALUE_FONT_PX + (i < rows.length - 1 ? STAT_ROW_GAP : 0);
  });
  height += STAT_CARD_PAD;

  const ctx = prepareContext(canvas, width, height);
  if (!ctx) {
    return null;
  }

  // Paint the card background over the default white fill.
  ctx.fillStyle = input.background;
  ctx.fillRect(0, 0, width, height);

  const left = STAT_CARD_PAD;
  let y = STAT_CARD_PAD;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = input.foreground;

  if (input.title) {
    ctx.font = `${STAT_TITLE_FONT_PX}px ${FONT_FAMILY}`;
    ctx.fillText(input.title, left, y + STAT_TITLE_FONT_PX);
    y += STAT_TITLE_FONT_PX + STAT_TITLE_GAP;
  }

  if (input.headline) {
    ctx.font = `bold ${STAT_HEADLINE_FONT_PX}px ${FONT_FAMILY}`;
    ctx.fillText(input.headline, left, y + STAT_HEADLINE_FONT_PX);
    y += STAT_HEADLINE_FONT_PX + (rows.length ? STAT_TITLE_GAP : 0);
  }

  for (let i = 0; i < rows.length; i++) {
    const baseline = y + STAT_ROW_VALUE_FONT_PX;
    ctx.font = `600 ${STAT_ROW_LABEL_FONT_PX}px ${FONT_FAMILY}`;
    const labelText = `${rows[i].label}:`;
    ctx.fillText(labelText, left, baseline);
    const labelWidth = ctx.measureText(labelText).width;
    ctx.font = `bold ${STAT_ROW_VALUE_FONT_PX}px ${FONT_FAMILY}`;
    ctx.fillText(rows[i].value, left + labelWidth + 8, baseline);
    y += STAT_ROW_VALUE_FONT_PX + (i < rows.length - 1 ? STAT_ROW_GAP : 0);
  }

  return canvas.toDataURL('image/png');
}

/**
 * Turn a widget's existing native export snapshot (`pdfNativeTable` or
 * `avgDurations` — the same data the PDF export uses) into a raster PNG snapshot
 * for the per-widget PNG download, without html2canvas. `translate` resolves the
 * title/column-header i18n keys. Returns null when the snapshot has no native
 * representation or no canvas is available.
 */
export function renderNativeSnapshotToPng(
  snapshot: StatisticsExportSnapshot,
  translate: (key: string) => string
): StatisticsExportSnapshot | null {
  const title = snapshot.omitPdfTitle ? undefined : translate(snapshot.titleKey);

  if (snapshot.pdfNativeTable) {
    const table = snapshot.pdfNativeTable;
    // No rows and no headline value means the widget had no data for the range;
    // skip it so the PNG download surfaces "produced no image" rather than
    // saving a header-only image.
    if (table.rows.length === 0 && !table.headlineValue) {
      return null;
    }
    const imageDataUrl = renderNativeTableToPng({
      title,
      headlineValue: table.headlineValue,
      columns: table.columns.map((col) => ({
        header: translate(col.headerKey),
        align: col.align,
        width: col.width,
      })),
      rows: table.rows,
      columnGroups: table.columnGroups,
    });
    return imageDataUrl
      ? { slug: snapshot.slug, titleKey: snapshot.titleKey, imageDataUrl }
      : null;
  }

  if (snapshot.avgDurations) {
    const data = snapshot.avgDurations;
    const imageDataUrl = renderAvgDurationsToPng({
      title,
      totalLabel: data.totalLabel,
      totalValue: data.totalValue,
      segments: data.segments,
    });
    return imageDataUrl
      ? { slug: snapshot.slug, titleKey: snapshot.titleKey, imageDataUrl }
      : null;
  }

  return null;
}

/**
 * Size the canvas, paint the white background, and return a context scaled to
 * CSS px. Renders at RENDER_SCALE for crispness but clamps the scale down so
 * neither dimension exceeds SAFE_CANVAS_MAX_PX — an unusually tall table would
 * otherwise blow past the browser's max canvas size and render blank.
 */
function prepareContext(
  canvas: HTMLCanvasElement,
  widthCss: number,
  heightCss: number
): CanvasRenderingContext2D | null {
  const scale = Math.min(RENDER_SCALE, SAFE_CANVAS_MAX_PX / Math.max(widthCss, heightCss));
  // Clamp after ceil: floating-point rounding of `dim * scale` can land just
  // above SAFE_CANVAS_MAX_PX (e.g. 16000.0001 → ceil 16001), which would defeat
  // the guard, so cap the backing-store dimensions explicitly.
  canvas.width = Math.min(SAFE_CANVAS_MAX_PX, Math.ceil(widthCss * scale));
  canvas.height = Math.min(SAFE_CANVAS_MAX_PX, Math.ceil(heightCss * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  ctx.scale(scale, scale);
  ctx.fillStyle = BACKGROUND;
  ctx.fillRect(0, 0, widthCss, heightCss);
  ctx.textBaseline = 'alphabetic';
  return ctx;
}
